import React from 'react';
import { Bot, BotStatus, ExchangePlatform } from '@/types/trader';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Eye, Pause, Play, Trash2, Users } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';

interface BotListItemProps {
  bot: Bot;
  onViewDetails: (botId: string) => void;
  onToggleStatus: (botId: string, currentStatus: BotStatus) => void;
  onDelete: (bot: Bot) => void;
}

export const BotListItem = ({
  bot,
  onViewDetails,
  onToggleStatus,
  onDelete
}: BotListItemProps) => {
  const { t } = useTranslation();

  const ensureExchangeArray = (exchange: string | string[] | ExchangePlatform[] | undefined): ExchangePlatform[] => {
    if (!exchange) return [];
    if (Array.isArray(exchange)) return exchange as ExchangePlatform[];
    return [exchange as ExchangePlatform];
  };

  const exchanges = ensureExchangeArray(bot.exchange);

  return (
    <div className="group flex flex-row items-center gap-3 py-2 px-4 rounded-lg bg-card border border-border hover:border-primary/50 transition-all duration-200">
      {/* Bot Identity */}
      <div className="flex items-center gap-2 min-w-0 flex-shrink-0">
        <h3 className="font-semibold text-sm truncate max-w-[180px]">{bot.name}</h3>
        <Badge 
          variant={bot.status === 'active' ? 'default' : 'secondary'}
          className={cn(
            "text-xs",
            bot.status === 'active' && "bg-green-500/10 text-green-500 hover:bg-green-500/20"
          )}
        >
          {bot.status === 'active' ? t('active') : t('inactive')}
        </Badge>
        <Badge variant="outline" className="text-xs">
          {bot.type}
        </Badge>
      </div>

      {/* Description */}
      <p className="text-xs text-muted-foreground truncate max-w-[200px] hidden lg:block">
        {bot.description}
      </p>

      {/* Stats */}
      <div className="flex items-center gap-3 ml-auto">
        {/* Exchanges */}
        {exchanges.length > 0 && (
          <div className="flex items-center gap-1 hidden md:flex">
            {exchanges.slice(0, 2).map((exchange, idx) => (
              <Badge 
                key={idx} 
                variant="outline" 
                className="text-xs bg-background/50"
              >
                {exchange}
              </Badge>
            ))}
            {exchanges.length > 2 && (
              <Badge variant="outline" className="text-xs bg-background/50">
                +{exchanges.length - 2}
              </Badge>
            )}
          </div>
        )}

        {/* Connected Accounts */}
        <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <Users size={14} />
          <span className="hidden sm:inline">{bot.connected_accounts_count || 0} {t('accounts')}</span>
          <span className="sm:hidden">{bot.connected_accounts_count || 0}</span>
        </div>
      </div>

      {/* Actions */}
      <div className="flex items-center gap-1.5 flex-shrink-0">
        <Button
          variant="outline"
          size="sm"
          onClick={() => onViewDetails(bot.id)}
          className="h-8 w-8 p-0"
        >
          <Eye size={14} />
        </Button>

        <Button
          variant="outline"
          size="sm"
          onClick={() => onToggleStatus(bot.id, bot.status)}
          className={cn(
            "h-8 w-8 p-0",
            bot.status === 'active' && "text-orange-500 hover:text-orange-600"
          )}
        >
          {bot.status === 'active' ? <Pause size={14} /> : <Play size={14} />}
        </Button>

        <Button
          variant="outline"
          size="sm"
          onClick={() => onDelete(bot)}
          className="h-8 w-8 p-0 text-destructive hover:text-destructive"
        >
          <Trash2 size={14} />
        </Button>
      </div>
    </div>
  );
};
